import { SelectOption } from 'global';
import { Controller } from 'react-hook-form';
import { Control } from 'react-hook-form/dist/types/form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';
import {
  TextField,
  Typography,
  Autocomplete,
  InputAdornment,
  TextFieldProps,
  CircularProgress,
  AutocompleteProps,
} from '@mui/material';

interface Props<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean = false,
  FormValues extends FieldValues = FieldValues,
> extends Omit<AutocompleteProps<Option, Multiple, false, false>, 'renderInput'> {
  control: Control<FormValues>;
  valueKey?: keyof SelectOption;
  labelKey?: keyof SelectOption;
  textFieldProps?: Partial<TextFieldProps>;
  name: UseControllerProps<FormValues>['name'];
  extraAction?: (nextValue: any) => void;
}

export const AutocompleteValueField = <
  Option extends SelectOption = SelectOption,
  Multiple extends boolean = false,
  FormValues extends FieldValues = FieldValues,
>({
  name,
  control,
  options,
  textFieldProps = {},
  valueKey = 'value',
  labelKey = 'label',
  loading = false,
  extraAction,
  ...other
}: Props<Option, Multiple, FormValues>) => {
  const isMultiple = other.multiple ?? false;
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ref }, fieldState }) => {
        const mapToOption = (val: string): Option | undefined => options.find(o => o[valueKey] === val);
        const mapToOptions = (val: string[]): Option[] =>
          val.map(v => options.find(o => o[valueKey] === v)).filter(Boolean) as Option[];
        const currentValue = isMultiple ? mapToOptions(value ?? []) : mapToOption(value ?? '');
        return (
          <Autocomplete<Option, Multiple, false, false>
            fullWidth
            loading={loading}
            options={options}
            getOptionLabel={option => option[labelKey] ?? ''}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li {...optionProps} key={key}>
                  <Typography>{option[labelKey]}</Typography>
                </li>
              );
            }}
            isOptionEqualToValue={(opt, val) => opt[valueKey] === val[valueKey]}
            value={(currentValue as any) || ''}
            onChange={(_, newVal) => {
              const value = isMultiple
                ? (newVal as Option[]).map(o => o[valueKey])
                : ((newVal as Option | null)?.[valueKey] ?? null);
              onChange(value);
              extraAction?.(value);
            }}
            renderInput={params => (
              <TextField
                {...params}
                inputRef={ref}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                {...textFieldProps}
              />
            )}
            {...other}
          />
        );
      }}
    />
  );
};
